import { useState } from 'react'
import { Tooltip } from '@/shared/components/Tooltip'
import { useMediaQuery } from '@/lib/hooks'
import { formatCount } from '@/lib/format'

/**
 * Severity bands for the Findings donut, worst-first. STATUS colours - a small reserved scale,
 * not a categorical series palette - and the same steps the Dashboard's FindingsChart ring uses,
 * so the two donuts in the product read as one system.
 *
 * `rest` aggregates medium + low because the artefact rollup carries only critical, high and a
 * total; it takes the neutral info step rather than medium's yellow, which would overstate a band
 * that is usually mostly lows.
 *
 * It also rests at low opacity, which is the emphasis pattern rather than decoration. Low-severity
 * findings are nearly always the most numerous, so at equal weight a provider with 12 criticals
 * and 154 lows draws as a mostly-neutral ring and the criticals vanish - the opposite of the
 * triage question the column answers. Saturated serious bands against a recessive tail keep
 * "how much of this is serious" legible at 28px; hover restores it to full strength.
 */
const SEVERITY_BANDS = [
  { key: 'critical', label: 'Critical', color: 'var(--red)', rest: 1 },
  { key: 'high', label: 'High', color: 'var(--sev-high)', rest: 1 },
  { key: 'rest', label: 'Medium & low', color: 'var(--sev-info)', rest: 0.4 },
] as const

/**
 * The Findings cell: a severity donut carrying the MIX, the total beside it carrying the
 * MAGNITUDE, and a keyed breakdown - one row per band, with its figure, share and label - in the
 * hover popover. Hovering an arc grows it, dims its siblings and lifts the matching row in the
 * popover; the popover is interactive, so a long readout can be read at leisure rather than
 * chased.
 *
 * The total is not decoration: a ring alone draws 2 findings and 200 identically, which is the
 * one thing the old `3C 2H 5` text run got right. `rest` is derived (total − critical − high)
 * from the authoritative rollup rather than summed from a findings list the list row never loads.
 *
 * `focusable={false}` for the version cell, whose enclosing button is already the tab stop and
 * already spells the counts out in its aria-label - a second stop inside it would be a trap.
 */
export function SeverityDonut({ critical, high, total, unfixableHigh, maxCvss, footnote, focusable = true, size = 28 }: {
  critical: number
  high: number
  total: number
  unfixableHigh?: boolean
  maxCvss?: number | null
  /** Extra context line under the breakdown - e.g. the artefact-list cell's "latest version
   *  only" scope note, so the ring is never read as covering every cached version. */
  footnote?: string
  focusable?: boolean
  size?: number
}) {
  const [hovered, setHovered] = useState<string | null>(null)
  // An inline SVG transition is out of reach of a CSS media query, so the grow/dim tween is gated
  // here (the colour swap itself still happens instantly). Same treatment as the Dashboard ring.
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)')

  // Reject non-finite / negative inputs BEFORE any arithmetic. These arrive from JSON (an older
  // worker omits the rollup entirely, so a caller spreading it hands us `undefined`), and the
  // natural-looking guard below does NOT catch them: `total === undefined` makes `rest` NaN,
  // `sum` NaN, and `NaN === 0` is false - so the component would render a bare track with no
  // arcs, `aria-label="NaN findings"` and a `-` figure in every row. Nothing is better than that.
  if (![critical, high, total].every((n) => Number.isFinite(n) && n >= 0)) return null

  // `total` is authoritative for the magnitude. Clamping the serious bands to it keeps the
  // headline figure and every percentage honest if a caller ever passes critical + high > total:
  // `sum` drives both, so an unclamped {5, 5, total: 4} would read "10 findings" at 50/50 and
  // invent two findings that do not exist.
  const crit = Math.min(critical, total)
  const hi = Math.min(high, Math.max(0, total - crit))
  const rest = Math.max(0, total - crit - hi)
  const value: Record<string, number> = { critical: crit, high: hi, rest }
  const sum = crit + hi + rest
  const bands = SEVERITY_BANDS.filter((b) => value[b.key]! > 0)
  if (sum === 0) return null

  // Only a finite number can be formatted; a string score from JSON would throw inside
  // `.toFixed()` DURING RENDER, which escapes to the app-level error boundary and replaces the
  // whole page with the error screen rather than degrading this one cell.
  const cvss = typeof maxCvss === 'number' && Number.isFinite(maxCvss) ? maxCvss : null

  const R = 40
  const C = 2 * Math.PI * R
  // Separate the wedges so the ring reads as bands rather than one continuous sweep. A
  // single-band ring skips the gap, which would otherwise notch an unbroken circle.
  const GAP = bands.length > 1 ? 4 : 0
  let offset = 0
  const segments = bands.map((b) => {
    const arc = (value[b.key]! / sum) * C
    const dash = Math.max(arc - GAP, 0.5)
    const seg = { ...b, count: value[b.key]!, dash, gap: C - dash, offset }
    offset += arc
    return seg
  })

  const pct = (n: number) => Math.round((n / sum) * 100)
  const spoken = `${segments.map((b) => `${b.count} ${b.label.toLowerCase()}`).join(', ')}${unfixableHigh ? ', no fix available for at least one high' : ''}${cvss != null ? `, worst CVSS ${cvss.toFixed(1)}` : ''}`

  // The popover IS the legend: every band named, counted and shared, so identity never rests on
  // colour alone (there is no room for a legend beside a 26px mark in a table cell).
  const tip = (
    <span style={{ display: 'grid', gap: 4, textAlign: 'left', minWidth: 172 }}>
      {segments.map((b) => (
        // The popover row doubles as the band's hover target, and is the RELIABLE one: a 6% wedge
        // is ~3px of arc at 28px, so the ring alone would leave the smallest - and most urgent -
        // band the hardest to point at. Interactive popover, so the cursor can reach these.
        <span
          key={b.key}
          onMouseEnter={() => setHovered(b.key)}
          onMouseLeave={() => setHovered(null)}
          style={{
            display: 'flex', alignItems: 'center', gap: 7, whiteSpace: 'nowrap', cursor: 'default',
            opacity: hovered !== null && hovered !== b.key ? 0.45 : 1,
          }}
        >
          {/* The key swatch stays at full strength: it identifies the band. The ring's resting
              opacity is emphasis, which belongs to the mark, not to the legend. */}
          <span aria-hidden style={{ width: 8, height: 8, borderRadius: 2, background: b.color, flexShrink: 0 }} />
          <span style={{ fontSize: '0.72rem', color: 'var(--text2)' }}>{b.label}</span>
          <span style={{ flex: 1 }} />
          <span style={{ fontFamily: 'var(--mono)', fontSize: '0.72rem', color: 'var(--text)' }}>{b.count}</span>
          <span style={{ fontFamily: 'var(--mono)', fontSize: '0.66rem', color: 'var(--text3)', width: 32, textAlign: 'right' }}>{pct(b.count)}%</span>
        </span>
      ))}
      {(unfixableHigh || cvss != null) && (
        <span style={{ display: 'flex', gap: 10, marginTop: 2, paddingTop: 4, borderTop: '1px solid var(--border2)', fontSize: '0.68rem', color: 'var(--text3)', whiteSpace: 'nowrap' }}>
          {cvss != null && <span>worst CVSS <span style={{ fontFamily: 'var(--mono)', color: 'var(--text2)' }}>{cvss.toFixed(1)}</span></span>}
          {unfixableHigh && <span style={{ color: 'var(--amber)' }}>no fix for at least one high</span>}
        </span>
      )}
      {footnote && (
        <span style={{ display: 'block', marginTop: 2, paddingTop: 4, borderTop: '1px solid var(--border2)', fontSize: '0.68rem', color: 'var(--text3)', whiteSpace: 'normal' }}>
          {footnote}
        </span>
      )}
    </span>
  )

  return (
    <Tooltip text={tip} placement="top" interactive maxWidth={300}>
      <span
        {...(focusable ? { tabIndex: 0 } : {})}
        role="img"
        aria-label={`${sum} finding${sum === 1 ? '' : 's'}: ${spoken}`}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 7, cursor: 'help', whiteSpace: 'nowrap' }}
      >
        <svg width={size} height={size} viewBox="0 0 100 100" style={{ flexShrink: 0, overflow: 'visible', display: 'block' }}>
          {/* Track: the ring stays a closed shape even when one band rounds to a sliver. */}
          <circle cx={50} cy={50} r={R} fill="none" stroke="var(--bg3)" strokeWidth={16} />
          {segments.map((b) => {
            const isHovered = hovered === b.key
            return (
              <circle
                key={b.key}
                cx={50} cy={50} r={R}
                fill="none"
                stroke={b.color}
                strokeWidth={isHovered ? 22 : 16}
                strokeDasharray={`${b.dash} ${b.gap}`}
                strokeDashoffset={-b.offset}
                strokeLinecap="butt"
                transform="rotate(-90 50 50)"
                opacity={hovered === null ? b.rest : isHovered ? 1 : 0.22}
                // The hit arcs below own the pointer; the paint must not shadow them, or growing
                // an arc under the cursor would re-fire enter/leave against its own new geometry.
                style={{ transition: reduceMotion ? 'none' : 'stroke-width 0.15s, opacity 0.15s', pointerEvents: 'none' }}
              />
            )
          })}
          {/* Hit targets, drawn last so they sit above the paint. The painted arc is barely 2px
              at this size - far under a usable target - so each band gets a transparent arc of
              its own at two-and-a-half times the width. `pointerEvents: stroke` hit-tests the
              band's wedge only, so the donut hole and the gaps stay click-through to the row. */}
          {segments.map((b) => (
            <circle
              key={`hit-${b.key}`}
              cx={50} cy={50} r={R}
              fill="none"
              stroke="transparent"
              strokeWidth={28}
              strokeDasharray={`${b.dash} ${b.gap}`}
              strokeDashoffset={-b.offset}
              strokeLinecap="butt"
              transform="rotate(-90 50 50)"
              style={{ pointerEvents: 'stroke', cursor: 'help' }}
              onMouseEnter={() => setHovered(b.key)}
              onMouseLeave={() => setHovered(null)}
            />
          ))}
        </svg>
        {/* Magnitude in ink, never a band colour - the mark beside it carries identity. */}
        <span style={{ fontFamily: 'var(--mono)', fontSize: '0.75rem', color: 'var(--text2)' }}>
          {formatCount(hovered ? (value[hovered] ?? sum) : sum)}
        </span>
        {/* Not part of the severity mix, so it stays out of the ring - but "a high nobody can fix"
            is its own triage signal and has to survive at a glance, not only in the popover. */}
        {unfixableHigh && (
          <span style={{ color: 'var(--amber)', fontSize: '0.68rem', border: '1px solid var(--amber)', borderRadius: 3, padding: '0 4px', lineHeight: 1.4 }}>
            no fix
          </span>
        )}
      </span>
    </Tooltip>
  )
}
